// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    getLink,
    getPageLinkByNodeId,
    getPageLinkByPageType,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import TippyPopup from "@rugby/App/Components/Tippy/TippyPopup";
import XngSignIn from "@rugby/App/Components/XngSignIn/XngSignIn";
import { xHeaderState } from "@rugby/App/Store/Reducers";
import { myAccountOverlayStyles } from "@rugby/Overrides/Widgets/Common/HeaderMyAccountOverlay.style";
import { headerSignInStyles } from "@rugby/Overrides/Widgets/Common/HeaderSignIn.style";
import Account from "@rugby/XDL/Icons/Account";
import Phone from "@rugby/XDL/Icons/Call";
import Mail from "@rugby/XDL/Icons/Email";
import { capitalize, isMobileDevice } from "@rugby/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface CustomLinkModel {
    fields: {
        isGroup: boolean;
        destination: LinkFieldValue;
        title: string;
    };
}

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    links = "links",
    isGroup = "isGroup",
}

const getLinks = (state: ApplicationState, ownProps: OwnProps, myAccountPageLink: any) => {
    const links = (ownProps.fields.links || []).map((linkItem: CustomLinkModel) => {
        const link: PageLinkModel = getLink(state, linkItem["fields"].destination) as PageLinkModel;
        let show = true;
        let exist = false;

        if (myAccountPageLink && myAccountPageLink?.children?.length) {
            // eslint-disable-next-line array-callback-return
            myAccountPageLink?.children.some((maLink: PageLinkModel) => {
                if (!linkItem.fields.isGroup) {
                    if (link && maLink.title === link.title) {
                        exist = true;
                        show = !maLink.excludeFromNavigation;
                    }
                }
            });
        }

        if (!exist || show) {
            return {
                url: link?.url || "",
                title: linkItem.fields.isGroup ? linkItem.fields.title : link?.title,
                isGroup: linkItem.fields.isGroup,
            };
        }

        return {};
    });
    return links;
};

const getPageLink = (state: ApplicationState, pageName: string) => {
    const page = getPageLinkByNodeId(state, state.links.pageTypesToNodeId[pageName]);
    return page?.url || "";
};

const mapStateToProps = (state: ApplicationState & xHeaderState, ownProps: OwnProps) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    isSigningIn: state.context.isSigningIn,
    userName: state.context.session.userName,
    dashboardPageUrl: getPageLink(state, "MyAccountPage"),
    session: state.context.session,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    links: getLinks(state, ownProps, getPageLinkByPageType(state, "MyAccountPage")),
    changeCustomerLink: getPageLink(state, "ChangeCustomerPage"),
    MyAccountLink: getPageLink(state, "MyAccountPage"),
    isSticky: state.xHeader.isSticky,
    isPunchOutSession: getIsPunchOutSession(state),
    currentShipTo: getCurrentShipToState(state).value,
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    currentLocation: getLocation(state),
    companyName: getBillToState(state, state.context.session.billToId)?.value?.companyName || "",
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    loadCurrentShipTo,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.links]: CustomLinkModel[];
    };
}
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const maStyles = myAccountOverlayStyles;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    isSignInPage,
    userName,
    session,
    dashboardPageUrl,
    isSigningIn,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    isPunchOutSession,
    fields,
    history,
    signInUrl,
    links,
    changeCustomerLink,
    MyAccountLink,
    isSticky,
    currentShipTo,
    loadCurrentShipTo,
    currentLocation,
    displayChangeCustomerLink,
    companyName,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
}: Props) => {
    const [isPopupVisible, setPopupVisibility] = useState(false);

    const defaultWarehouse = JSON.parse(session.properties["defaultWarehouse"] || "{}");
    const primarySalesperson = JSON.parse(session.properties["primarySalesperson"] || "{}");

    React.useEffect(() => {
        if (session.isAuthenticated && !currentShipTo && session.billToId && session.shipToId) {
            loadCurrentShipTo();
        }
    }, []);

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        hideTippy();
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const onPopupVisibilityUpdate = (popupVisibility: boolean) => {
        setPopupVisibility(popupVisibility);
    };

    const headerElement = (isSignIn: boolean) => {
        const url = isSignIn ? signInUrl : dashboardPageUrl;
        const messages = isSignIn
            ? ["Sign In /", "Register"]
            : [`Hello${session.firstName && `,`}`, `${session.firstName}`];

        return (
            <Link
                {...styles.signInLink}
                onClick={() => {
                    !isMobileDevice() && history.push(url ?? "");
                }}
            >
                <Icon {...styles.userIcon} src={Account} {...{ isActive: isPopupVisible }} />
                {!isSticky && (
                    <StyledWrapper className="menu-icon-text">
                        {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                        <Typography {...styles.signInText}>{translate(messages[0])}</Typography>
                        {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                        <Typography {...styles.registerText}>{translate(messages[1])}</Typography>
                    </StyledWrapper>
                )}
            </Link>
        );
    };

    const welcomeMessage = () => {
        let message = translate("Welcome");
        message += `${companyName ? `, ${capitalize(companyName || "")}!` : "!"}`;
        return message;
    };

    const MyAccountOverlay = () => {
        return (
            <StyledWrapper {...maStyles.MyAccountWrapper}>
                <StyledWrapper {...maStyles.titleWrapper}>
                    <Link {...maStyles.mainTitle} href={MyAccountLink} onClick={() => hideTippy()}>
                        {translate("My Account")}
                    </Link>

                    <Typography as="div" {...maStyles.secondTitle}>
                        {welcomeMessage()}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper style={{ display: "flex", flexWrap: "wrap" }}>
                    {links.map((link: any) => {
                        if (link?.isGroup) {
                            return (
                                <Typography as="div" {...maStyles.groupTitle} key={uniqueId()}>
                                    {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                                    {translate(link.title)}
                                </Typography>
                            );
                        }
                        if (link && link.url) {
                            return (
                                <Link
                                    {...maStyles.link}
                                    key={uniqueId()}
                                    href={link.url}
                                    onClick={() => {
                                        hideTippy();
                                    }}
                                >
                                    {link.title}
                                </Link>
                            );
                        }
                        return null;
                    })}
                </StyledWrapper>
                <StyledWrapper>
                    <Typography as="div" {...maStyles.groupTitle}>
                        {translate("My Local branch")}
                    </Typography>
                    <StyledWrapper as="div" {...maStyles.localBranchWrapper}>
                        <StyledWrapper as="div">
                            <Typography {...maStyles.localBranchInfo}>{defaultWarehouse.address1}</Typography>
                            <Typography {...maStyles.localBranchInfo}>
                                {defaultWarehouse.city && `${defaultWarehouse.city},`} {defaultWarehouse.state}
                            </Typography>
                            <Typography {...maStyles.localBranchInfo}>
                                {defaultWarehouse.postalCode && `${defaultWarehouse.postalCode},`}
                                {defaultWarehouse.country}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper as="div">
                            {primarySalesperson.email && (
                                <Typography {...maStyles.localBranchInfo}>
                                    <Icon {...maStyles.icons} src={Mail} />
                                    <Typography
                                        color="text.link"
                                        as="a"
                                        {...{ href: `mailto:${primarySalesperson.email}` }}
                                    >
                                        {primarySalesperson.email}
                                    </Typography>
                                </Typography>
                            )}
                            {primarySalesperson.phone1 && (
                                <Typography {...maStyles.localBranchInfo}>
                                    <Icon {...maStyles.icons} src={Phone} />
                                    <Typography
                                        color="text.link"
                                        as="a"
                                        {...{ href: `tel:${primarySalesperson.phone1}` }}
                                    >
                                        {primarySalesperson.phone1}
                                    </Typography>
                                </Typography>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper
                    {...{ displayChangeCustomerLink: session.displayChangeCustomerLink }}
                    {...maStyles.buttonsWrapper}
                >
                    {session.displayChangeCustomerLink && (
                        <Button
                            {...maStyles.buttons}
                            sizeVariant="medium"
                            onClick={() => {
                                hideTippy();
                                history.push(changeCustomerLink);
                            }}
                        >
                            {translate("Change Ship-To")}
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        sizeVariant="medium"
                        {...maStyles.buttons}
                        onClick={onSignOutHandler}
                        data-test-selector="headerSignOutLink"
                    >
                        {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {(!fields.includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <TippyPopup
                    updateTriggerOptionsOnMobile
                    content={<XngSignIn />}
                    onPopupVisibilityUpdate={onPopupVisibilityUpdate}
                    extendedStyles={styles.tippyStyle}
                >
                    {headerElement(true)}
                </TippyPopup>
            )}

            {fields.includeAccountMenu && userName && !currentUserIsGuest && (
                <TippyPopup
                    updateTriggerOptionsOnMobile
                    content={MyAccountOverlay()}
                    onPopupVisibilityUpdate={onPopupVisibilityUpdate}
                >
                    {headerElement(false)}
                </TippyPopup>
            )}
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),

    definition: {
        displayName: "Header Sign In / My account links",
        fieldDefinitions: [
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;

                    if (item.fields[fields.isGroup]) {
                        return `Group Name: ${item.fields.title}`;
                    }

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value;
                    }
                    if (type === "Url") {
                        return value;
                    }

                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 4,
                fieldDefinitions: [
                    {
                        name: fields.isGroup,
                        displayName: "Is group",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: item => !item.fields[fields.isGroup],
                    },
                    {
                        name: "title",
                        displayName: "Groupe Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields[fields.isGroup] === true,
                    },
                ],
            },
        ],
        group: "Common",
    },
};
export default widgetModule;
